import { useParams } from 'react-router';

import { Grid } from '@mui/material';
import { DatePicker, LocalizationProvider } from '@mui/x-date-pickers';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { ptBR } from 'date-fns/locale';
import { getIn, useFormikContext } from 'formik';

import { CissMaskField, CissNumberField, CissTextField } from 'components/fields';
import { Row } from 'components/form';
import { Block } from 'components/page';
import { CepField } from 'pages/cadastro/usuario/components';
import { TipoRevendedorAutoComplete } from 'pages/revendedor/revendedor/components';
import { Revendedor } from 'pages/revendedor/revendedor/types';

export function RevendedorGeneralBlock() {
    const { id } = useParams();
    const { values, errors, setFieldValue } = useFormikContext<Revendedor>();
    const isPessoaFisica = values.tipoRevendedor?.tipoRevendedorEnum === 'PF';
    const isPessoaJuridica = values.tipoRevendedor?.tipoRevendedorEnum === 'PJ';

    return (
        <Block>
            <Row>
                <Grid sx={{ flex: 1 }}>
                    <CissTextField label="Código" disabled={true} error={errors.idRevendedor} value={values.idRevendedor} />
                </Grid>

                <Grid sx={{ flex: 1 }}>
                    <TipoRevendedorAutoComplete
                        name="tipoRevendedor"
                        disabled={Boolean(id)}
                        value={values.tipoRevendedor}
                        error={getIn(errors, 'tipoRevendedor')}
                        onChange={(e, value) => {
                            setFieldValue('tipoRevendedor', value);
                            setFieldValue('dsDocumento', '');
                        }}
                    />
                </Grid>

                <Grid sx={{ flex: 1 }}>
                    <CissTextField
                        label={isPessoaJuridica ? 'Nome fantasia' : 'Nome completo'}
                        name="dsNome"
                        error={errors.dsNome}
                        value={values.dsNome}
                        onChange={(e) => setFieldValue('dsNome', e.target.value)}
                    />
                </Grid>
            </Row>
            <Row>
                {isPessoaFisica && (
                    <LocalizationProvider adapterLocale={ptBR} dateAdapter={AdapterDateFns}>
                        <DatePicker
                            label="Data de nascimento"
                            format="dd/MM/yyyy"
                            sx={{ flex: 1 }}
                            value={values.dtNascimento ? new Date(values.dtNascimento) : null}
                            onChange={(value) => setFieldValue('dtNascimento', value?.toISOString())}
                            slotProps={{
                                textField: {
                                    name: 'dtNascimento',
                                    error: Boolean(errors.dtNascimento),
                                    helperText: errors.dtNascimento,
                                },
                            }}
                        />
                    </LocalizationProvider>
                )}

                {isPessoaJuridica && (
                    <Grid sx={{ flex: 1 }}>
                        <CissTextField
                            label="Inscrição estadual"
                            name="dsIe"
                            error={errors.dsIe}
                            value={values.dsIe}
                            onChange={(e) => setFieldValue('dsIe', e.target.value)}
                        />
                    </Grid>
                )}

                <Grid sx={{ flex: 1 }}>
                    <CissMaskField
                        label="Documento"
                        name="documento"
                        format={isPessoaJuridica ? 'cnpj' : 'cpf'}
                        disabled={Boolean(id)}
                        value={values.dsDocumento}
                        error={errors.dsDocumento}
                        onChange={(e) => setFieldValue('dsDocumento', e.target.value)}
                    />
                </Grid>

                <Grid sx={{ flex: 1 }}>
                    <CissTextField
                        label="E-mail"
                        name="dsEmail"
                        value={values.dsEmail}
                        error={errors.dsEmail}
                        onChange={(e) => setFieldValue('dsEmail', e.target.value)}
                    />
                </Grid>

                <Grid sx={{ flex: 1 }}>
                    <CissMaskField
                        label="Telefone"
                        name="dsTelefone"
                        format="phone"
                        value={values.dsTelefone}
                        onChange={(e) => setFieldValue('dsTelefone', e.target.value)}
                        error={errors.dsTelefone}
                    />
                </Grid>
            </Row>

            <Row>
                <Grid sx={{ flex: 1 }}>
                    <CepField value={values.cep?.nrCep || ''} error={getIn(errors, 'cep.nrCep')} />
                </Grid>

                <Grid sx={{ flex: 2 }}>
                    <CissTextField
                        label="Endereço"
                        name="dsCelular"
                        value={values.dsEndereco}
                        error={errors.dsEndereco}
                        disabled={!values.cep?.cidade?.dsCidade}
                        onChange={(e) => setFieldValue('dsEndereco', e.target.value || null)}
                    />
                </Grid>

                <Grid sx={{ flex: 1 }}>
                    <CissNumberField
                        label="Número"
                        name="nrEndereco"
                        preventEmptyField={false}
                        value={values.nrEndereco}
                        error={errors.nrEndereco}
                        disabled={!values.cep?.cidade?.dsCidade}
                        onChange={(e) => setFieldValue('nrEndereco', e.target.value || null)}
                    />
                </Grid>

                <Grid sx={{ flex: 1 }}>
                    <CissTextField
                        label="Complemento"
                        name="dsComplemento"
                        value={values.dsComplemento}
                        error={errors.dsComplemento}
                        disabled={!values.cep?.cidade?.dsCidade}
                        onChange={(e) => setFieldValue('dsComplemento', e.target.value || null)}
                    />
                </Grid>
            </Row>

            <Row>
                <Grid sx={{ flex: 1 }}>
                    <CissTextField
                        label="Bairro"
                        name="dsBairro"
                        value={values.dsBairro}
                        error={errors.dsBairro}
                        disabled={!values.cep?.cidade?.dsCidade}
                        onChange={(e) => setFieldValue('dsBairro', e.target.value || null)}
                    />
                </Grid>

                <Grid sx={{ flex: 1 }}>
                    <CissTextField
                        disabled
                        label="Estado"
                        name="dsEstado"
                        value={values.cep?.estado?.dsEstado || ''}
                        error={getIn(errors, 'cep.estado')}
                    />
                </Grid>

                <Grid sx={{ flex: 1 }}>
                    <CissTextField
                        disabled
                        label="Cidade"
                        name="dsCidade"
                        value={values.cep?.cidade?.dsCidade || ''}
                        error={getIn(errors, 'cep.cidade')}
                    />
                </Grid>
            </Row>
        </Block>
    );
}
